import { fileExtension } from './modelPackagePlan';
import { scanObj } from './scanObj';

const readText = file => typeof file.text === 'function' ? file.text() : new Promise((resolve, reject) => { const reader = new FileReader(); reader.onload = () => resolve(reader.result); reader.onerror = reject; reader.readAsText(file); });

// Package integrity validation only. Capability remains in fileTypeMap/resolver.
export async function validateModelPackage(root, companions) {
  const files = [root, ...companions];
  if (files.length > 201 || files.some(file => !file || typeof file.name !== 'string' || !file.name || file.name.length > 255 || Array.from(file.name).some(character => character === '/' || character === '\\' || character.charCodeAt(0) < 32) || !Number.isFinite(file.size) || file.size <= 0 || file.size > 5 * 1024 ** 3)) throw new Error('Choose nonempty files with safe names within the supported upload size.');
  const names = files.map(file => file.name.toLowerCase());
  if (new Set(names).size !== names.length) throw new Error('Two package files share a basename. Rename them and update the model references before importing.');
  const find = (value, { encodedUri = false } = {}) => {
    if (typeof value !== 'string') throw new Error('Model reference must be a filename.');
    if (/^data:/i.test(value)) return;
    // glTF URIs use percent-encoding; OBJ and MTL text references do not.
    // Never decode native File names such as '100%.mtl'.
    let decoded = value;
    if (encodedUri) {
      try { decoded = decodeURIComponent(value); }
      catch { throw new Error('Model reference has invalid URI encoding.'); }
    }
    decoded = decoded.replace(/\\/g, '/');
    if (/^[a-z][a-z0-9+.-]*:/i.test(decoded) || decoded.startsWith('/') || decoded.split('/').includes('..')) throw new Error('Model companions must be included local files with safe relative paths.');
    const name = decoded.replace(/^\.\//, '').split('/').pop();
    if (!companions.some(file => file.name === name)) {
      if (fileExtension(root.name) === 'obj' && fileExtension(name) === 'mtl') return;
      throw new Error(`Required companion "${name}" is missing. Choose the complete model package before importing.`);
    }
  };
  const ext = fileExtension(root.name);
  if (ext === 'gltf' && root.size > 32 * 1024 ** 2) throw new Error('This text model is too large for safe package validation. Supply a standalone GLB or a smaller complete package.');
  if (ext === 'gltf') {
    let json; try { json = JSON.parse(await readText(root)); } catch { throw new Error('The glTF root is corrupt or invalid JSON. Choose a complete valid model package.'); }
    if (json.asset?.version !== '2.0' || !Array.isArray(json.scenes)) throw new Error('Use a valid glTF 2.0 model.');
    for (const row of [...(json.buffers || []), ...(json.images || [])]) if (row.uri) find(row.uri, { encodedUri: true });
  }
  if (ext === 'obj') {
    for (const value of (await scanObj(root)).references) { if (companions.some(file=>file.name===value)) find(value); else value.split(/\s+/).forEach(find); }
    for (const file of companions.filter(file => fileExtension(file.name) === 'mtl')) {
      if (file.size > 4 * 1024 ** 2) throw new Error('The material file is too large to validate.');
      for (const line of (await readText(file)).split(/\r?\n/)) if (/^\s*(?:map_\w+|bump|disp|decal|norm)\s+/i.test(line)) { const value=line.replace(/^\s*\S+\s+/,'').trim(); find(companions.some(row=>row.name===value)?value:value.split(/\s+/).pop()); }
    }
  }
}
